import { NavLink, Outlet, Link } from "react-router-dom";
import {
  Gauge,
  Wrench,
  Anchor,
  Waves,
  Shield,
  Droplets,
  Beaker,
  ClipboardList,
  Ruler,
  Save,
  Terminal,
  Activity,
  CreditCard,
  LogIn,
  LogOut,
  User as UserIcon,
  Library,
  Users,
  Gift,
  BookOpen,
  Compass,
  AlertTriangle,
  ClipboardCheck,
  Wrench as WrenchIcon,
  FlaskConical,
  Sigma,
  Menu,
  X,
} from "lucide-react";
import { useUnits } from "@/context/UnitsContext";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import AIAssistant from "@/components/AIAssistant";
import LaunchBanner from "@/components/LaunchBanner";
import { DealBanner } from "@/components/DealBanner";
import { useLocation } from "react-router-dom";

const NAV = [
  { to: "/", label: "Overview", icon: Activity, end: true },
  { to: "/pressure-control", label: "Pressure Control", icon: Gauge },
  { to: "/kill-sheet", label: "Kill Sheet", icon: ClipboardList },
  { to: "/tally", label: "Tally Sheet", icon: Ruler },
  { to: "/kick-tolerance", label: "Kick Tolerance", icon: AlertTriangle },
  { to: "/tubing-movement", label: "Tubing Movement", icon: Wrench },
  { to: "/directional", label: "Directional", icon: Compass },
  { to: "/torque-drag", label: "Torque & Drag", icon: Sigma },
  { to: "/free-point", label: "Free Point", icon: Anchor },
  { to: "/fishing", label: "Fishing", icon: WrenchIcon },
  { to: "/collapse-burst", label: "Collapse / Burst", icon: Shield },
  { to: "/hydrostatic", label: "Hydrostatic", icon: Waves },
  { to: "/capacities", label: "Capacities & Volumes", icon: Beaker },
  { to: "/mud-weight", label: "Mud Weight", icon: Droplets },
  { to: "/cementing", label: "Cementing", icon: FlaskConical },
  { to: "/casing-wizard", label: "Casing Wizard", icon: ClipboardCheck },
  { to: "/specs", label: "Spec Library", icon: Library },
  { to: "/saved", label: "Saved Jobs", icon: Save },
  { to: "/team", label: "Team Library", icon: Users },
];

function planBadge(user) {
  if (!user || user === false) return null;
  const p = user.plan;
  if (p === "trial" && user.trial_ends_at) {
    const days = Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / 86400000));
    return { label: `TRIAL · ${days}D`, color: "#fbbf24" };
  }
  if (p === "pro") return { label: "PRO", color: "#f59e0b" };
  if (p === "team") return { label: "TEAM", color: "#f59e0b" };
  if (p === "lifetime") return { label: "LIFETIME", color: "#10b981" };
  if (p === "guides") return { label: "GUIDES", color: "#38bdf8" };
  return { label: "FREE", color: "#64748b" };
}

function SidebarContent({ onNav }) {
  const { units, setUnits } = useUnits();
  return (
    <>
      <Link to="/" onClick={onNav} data-testid="brand-link" className="p-5 border-b border-[#334155] block hover:bg-[#0b0e13] transition-colors">
        <div className="flex items-center gap-3">
          <img
            src="/icon-192.png"
            alt="RIGCALC"
            data-testid="brand-logo"
            className="w-10 h-10 shrink-0"
          />
          <div>
            <div className="heading text-lg font-bold leading-none">RIGCALC</div>
            <div className="text-[10px] text-[#94a3b8] mono tracking-widest">DOWNHOLE OPS v1.0</div>
          </div>
        </div>
      </Link>

      <nav className="flex-1 py-3 overflow-y-auto">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNav}
              data-testid={`nav-${item.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors duration-150 ${
                  isActive
                    ? "border-[#f59e0b] bg-[#0b0e13] text-[#f59e0b]"
                    : "border-transparent text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#0b0e13]"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}

        <div className="mt-4 pt-4 border-t border-[#334155]">
          <NavLink to="/tools" onClick={onNav} data-testid="nav-tools" className={({ isActive }) => `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors duration-150 ${isActive ? "border-[#f59e0b] bg-[#0b0e13] text-[#f59e0b]" : "border-transparent text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#0b0e13]"}`}>
            <BookOpen className="w-4 h-4" />
            <span>Formula Guides</span>
          </NavLink>
          <NavLink to="/referrals" onClick={onNav} data-testid="nav-referrals" className={({ isActive }) => `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors duration-150 ${isActive ? "border-[#f59e0b] bg-[#0b0e13] text-[#f59e0b]" : "border-transparent text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#0b0e13]"}`}>
            <Gift className="w-4 h-4" />
            <span>Refer · Earn $10</span>
          </NavLink>
          <NavLink to="/pricing" onClick={onNav} data-testid="nav-pricing" className={({ isActive }) => `flex items-center gap-3 px-5 py-2.5 text-sm border-l-2 transition-colors duration-150 ${isActive ? "border-[#f59e0b] bg-[#0b0e13] text-[#f59e0b]" : "border-transparent text-[#94a3b8] hover:text-[#f8fafc] hover:bg-[#0b0e13]"}`}>
            <CreditCard className="w-4 h-4" />
            <span>Pricing</span>
          </NavLink>
        </div>
      </nav>

      <div className="p-4 border-t border-[#334155]">
        <div className="text-[10px] text-[#94a3b8] mono tracking-widest mb-2">UNIT SYSTEM</div>
        <div className="flex border border-[#334155]">
          <button
            data-testid="units-field-btn"
            onClick={() => setUnits("field")}
            className={`flex-1 py-1.5 text-xs mono tracking-wider transition-colors ${
              units === "field" ? "bg-[#f59e0b] text-[#0b0e13]" : "text-[#94a3b8] hover:text-[#f8fafc]"
            }`}
          >
            FIELD
          </button>
          <button
            data-testid="units-metric-btn"
            onClick={() => setUnits("metric")}
            className={`flex-1 py-1.5 text-xs mono tracking-wider transition-colors ${
              units === "metric" ? "bg-[#f59e0b] text-[#0b0e13]" : "text-[#94a3b8] hover:text-[#f8fafc]"
            }`}
          >
            METRIC
          </button>
        </div>
      </div>
    </>
  );
}

export default function Layout() {
  const { units } = useUnits();
  const { user, isAuthed, logout, setShowAuth } = useAuth();
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const badge = planBadge(user);
  const location = useLocation();

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#0b0e13] text-[#f8fafc]">
      {/* Desktop sidebar */}
      <aside data-testid="sidebar-desktop" className="hidden lg:flex w-64 shrink-0 border-r border-[#334155] bg-[#11151c] flex-col">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <aside
            data-testid="sidebar-mobile"
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-y-0 left-0 w-72 bg-[#11151c] border-r border-[#334155] flex flex-col"
          >
            <SidebarContent onNav={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <main className="flex-1 flex flex-col min-w-0">
        <LaunchBanner />
        <DealBanner />
        <header className="border-b border-[#334155] bg-[#11151c] px-4 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <button
              data-testid="mobile-menu-btn"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-[#f8fafc] hover:text-[#f59e0b] p-1"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden sm:flex items-center gap-3 text-xs mono text-[#94a3b8] min-w-0 truncate">
              <span className="inline-block w-1.5 h-1.5 bg-[#10b981] shrink-0" />
              <span className="tracking-widest hidden md:inline">FIELD READY</span>
              <span className="text-[#334155] hidden md:inline">|</span>
              <span className="tracking-widest truncate">{units === "field" ? "US OILFIELD" : "METRIC / SI"}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {badge && (
              <div
                data-testid="plan-badge"
                className="border px-2 py-1 text-[10px] mono tracking-widest"
                style={{ borderColor: badge.color, color: badge.color }}
              >
                {badge.label}
              </div>
            )}
            <button
              data-testid="open-ai-btn"
              onClick={() => setAiOpen(!aiOpen)}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b] text-xs mono tracking-widest transition-colors"
            >
              <Terminal className="w-3.5 h-3.5" />
              AI {aiOpen ? "[ON]" : "[OFF]"}
            </button>
            <button
              data-testid="open-ai-btn-mobile"
              onClick={() => setAiOpen(!aiOpen)}
              className="sm:hidden p-1.5 border border-[#334155] hover:border-[#f59e0b] hover:text-[#f59e0b]"
            >
              <Terminal className="w-4 h-4" />
            </button>
            {isAuthed ? (
              <>
                <div className="hidden md:flex items-center gap-1.5 text-xs mono text-[#94a3b8]">
                  <UserIcon className="w-3.5 h-3.5" />
                  <span className="truncate max-w-[120px]" data-testid="user-email">{user.email}</span>
                </div>
                <button
                  data-testid="logout-btn"
                  onClick={logout}
                  className="p-1.5 border border-[#334155] hover:border-[#ef4444] hover:text-[#ef4444] transition-colors"
                  aria-label="Sign out"
                >
                  <LogOut className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <button
                data-testid="signin-btn"
                onClick={() => setShowAuth(true)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[#f59e0b] hover:bg-[#fbbf24] text-[#0b0e13] text-xs mono tracking-widest font-bold transition-colors"
              >
                <LogIn className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">SIGN IN</span>
              </button>
            )}
          </div>
        </header>

        <div className="flex-1 flex min-h-0 relative">
          <div className="flex-1 overflow-y-auto p-4 lg:p-8 bg-grid">
            <Outlet />
          </div>
          {aiOpen && (
            <div className="fixed inset-y-0 right-0 z-30 lg:static lg:z-auto w-full lg:w-[400px]">
              <AIAssistant onClose={() => setAiOpen(false)} />
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
